/**
 * Security slice — Redux Toolkit state + thunks for the customer-portal
 * `/account/security` page (SF-ACCT-009).
 *
 * Scope: MFA enrollment + trusted-device management for the authenticated
 * customer. Six async operations are exposed:
 *   - `fetchMfaStatus`         — `GET /api/v1/account/mfa`
 *   - `enableMfa`              — `POST /api/v1/account/mfa/enable`
 *   - `confirmMfa`             — `POST /api/v1/account/mfa/confirm`
 *   - `disableMfa`             — `DELETE /api/v1/account/mfa`
 *   - `fetchTrustedDevices`    — `GET /api/v1/account/trusted-devices`
 *   - `revokeTrustedDevice`    — `DELETE /api/v1/account/trusted-devices/:id`
 *
 * Every thunk owns its own loading + error state so a slow / failed
 * status fetch never blocks the trusted-devices card from rendering.
 *
 * Side effects on top of the slice:
 *  - After `confirmMfa.fulfilled` and `disableMfa.fulfilled`, callers
 *    should dispatch `authSlice.setMfaEnabled(boolean)` to keep the
 *    in-memory user session aligned with the new enrollment state.
 *  - `revokeTrustedDevice` is implemented as an optimistic mutation —
 *    the device is filtered out of `trustedDevices` on `pending` and
 *    restored on `rejected` so the row reappears if the API call fails.
 *
 * SF-ACCT-009.
 */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import * as accountApi from '@dfq/shared/api/account';
import type {
  AccountMfaStatus,
  MfaConfirmationResult,
  MfaEnrollmentChallenge,
  TrustedDevice,
} from '@dfq/shared/api/account';
import { isApiError, type ApiError } from '@dfq/shared/api/_envelope';

// ----------------------------------------------------------------------------
// State shape
// ----------------------------------------------------------------------------

export type SecuritySectionStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface SecuritySectionError {
  message: string;
  /** HTTP status code, when known. 0 for non-axios errors. */
  status?: number;
  /** Backend error code (e.g. `INVALID_TOTP`), when present. */
  code?: string;
}

/**
 * UI step for the MFA enrollment flow. The page reads this to render
 * the right card body without coupling to thunk status.
 *
 *  - `disabled`   — MFA off; "Enable MFA" CTA visible.
 *  - `provisioned` — `enableMfa` resolved; QR code + secret + TOTP input visible.
 *  - `enabled`    — `confirmMfa` resolved (or initial fetch said so);
 *                   "Disable MFA" CTA visible.
 *  - `disabling`  — disable confirmation form is open.
 */
export type MfaUiStep = 'disabled' | 'provisioned' | 'enabled' | 'disabling';

export interface SecurityState {
  /** Latest MFA status fetched from the platform. `null` until first fetch resolves. */
  mfaStatus: AccountMfaStatus | null;
  mfaStatusFetch: SecuritySectionStatus;
  mfaStatusError: SecuritySectionError | null;

  /**
   * Provisioning challenge from `POST /account/mfa/enable`. Null when
   * the user has not started enrollment OR after a successful confirm /
   * cancel. Holds the QR-code URI + plain-text secret.
   */
  mfaChallenge: MfaEnrollmentChallenge | null;
  mfaEnableStatus: SecuritySectionStatus;
  mfaEnableError: SecuritySectionError | null;

  mfaConfirmStatus: SecuritySectionStatus;
  mfaConfirmError: SecuritySectionError | null;

  mfaDisableStatus: SecuritySectionStatus;
  mfaDisableError: SecuritySectionError | null;

  /**
   * Backup codes returned by `POST /account/mfa/confirm`. Cleared once
   * the user dismisses the dialog (via `clearMfaBackupCodes`).
   */
  mfaBackupCodes: string[] | null;

  trustedDevices: TrustedDevice[];
  trustedDevicesStatus: SecuritySectionStatus;
  trustedDevicesError: SecuritySectionError | null;

  /**
   * Per-device revoke action status keyed by device id. A device row
   * inspects the value at its own id to render an inline spinner /
   * error without disabling sibling rows.
   */
  trustedDeviceRevokeStatus: Record<number, SecuritySectionStatus>;
  trustedDeviceRevokeError: Record<number, SecuritySectionError | null>;

  /**
   * UI step for the MFA card. Derived in part from `mfaStatus.isEnabled`
   * + `mfaChallenge`, but exposed as state so a tester can preload the
   * card into any step.
   */
  mfaUiStep: MfaUiStep;
}

const initialState: SecurityState = {
  mfaStatus: null,
  mfaStatusFetch: 'idle',
  mfaStatusError: null,

  mfaChallenge: null,
  mfaEnableStatus: 'idle',
  mfaEnableError: null,

  mfaConfirmStatus: 'idle',
  mfaConfirmError: null,

  mfaDisableStatus: 'idle',
  mfaDisableError: null,

  mfaBackupCodes: null,

  trustedDevices: [],
  trustedDevicesStatus: 'idle',
  trustedDevicesError: null,

  trustedDeviceRevokeStatus: {},
  trustedDeviceRevokeError: {},

  mfaUiStep: 'disabled',
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function toSectionError(err: unknown): SecuritySectionError {
  if (isApiError(err)) {
    const apiErr = err as ApiError;
    return {
      message: apiErr.message,
      status: apiErr.status,
      code: apiErr.errorCode,
    };
  }
  return {
    message: err instanceof Error ? err.message : 'Request failed',
  };
}

// ----------------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------------

/**
 * Fetch the authenticated customer's MFA enrollment status.
 */
export const fetchMfaStatus = createAsyncThunk<
  AccountMfaStatus,
  void,
  { rejectValue: SecuritySectionError }
>('security/fetchMfaStatus', async (_, { rejectWithValue }) => {
  try {
    return await accountApi.getAccountMfaStatus();
  } catch (err) {
    return rejectWithValue(toSectionError(err));
  }
});

/**
 * Initiate MFA enrollment. The fulfilled response is rendered as a QR
 * code + manual-entry secret; the user then submits a TOTP code via
 * `confirmMfa`.
 */
export const enableMfa = createAsyncThunk<
  MfaEnrollmentChallenge,
  void,
  { rejectValue: SecuritySectionError }
>('security/enableMfa', async (_, { rejectWithValue }) => {
  try {
    return await accountApi.enableAccountMfa();
  } catch (err) {
    return rejectWithValue(toSectionError(err));
  }
});

/**
 * Confirm MFA enrollment with a 6-digit TOTP code. Fulfilled payload
 * carries the one-time backup codes — these are shown to the customer
 * once and not retrievable again.
 */
export const confirmMfa = createAsyncThunk<
  MfaConfirmationResult,
  string,
  { rejectValue: SecuritySectionError; state: SecuritySliceRoot }
>('security/confirmMfa', async (totpCode, { rejectWithValue, getState }) => {
  try {
    await accountApi.confirmAccountMfa(totpCode);
    return { backupCodes: getState().security.mfaChallenge?.recoveryCodes ?? [] };
  } catch (err) {
    return rejectWithValue(toSectionError(err));
  }
});

/**
 * Disable MFA. Requires a valid current TOTP code in the request body.
 * On success, the platform also invalidates all trusted-device tokens
 * for the user; the slice clears `trustedDevices` accordingly.
 */
export const disableMfa = createAsyncThunk<
  void,
  string,
  { rejectValue: SecuritySectionError }
>('security/disableMfa', async (totpCode, { rejectWithValue }) => {
  try {
    await accountApi.disableAccountMfa(totpCode);
  } catch (err) {
    return rejectWithValue(toSectionError(err));
  }
});

/**
 * Fetch active trusted devices for the authenticated customer.
 */
export const fetchTrustedDevices = createAsyncThunk<
  { devices: TrustedDevice[] },
  void,
  { rejectValue: SecuritySectionError }
>('security/fetchTrustedDevices', async (_, { rejectWithValue }) => {
  try {
    return await accountApi.getAccountTrustedDevices();
  } catch (err) {
    return rejectWithValue(toSectionError(err));
  }
});

/**
 * Revoke a single trusted device. Optimistic — the row is removed from
 * `trustedDevices` on `pending` and restored on `rejected`.
 */
export const revokeTrustedDevice = createAsyncThunk<
  { id: number },
  number,
  {
    state: { security: SecurityState };
    rejectValue: SecuritySectionError;
    rejectMeta: { id: number };
  }
>('security/revokeTrustedDevice', async (id, { rejectWithValue }) => {
  try {
    return await accountApi.revokeAccountTrustedDevice(id);
  } catch (err) {
    return rejectWithValue(toSectionError(err));
  }
});

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------

const securitySlice = createSlice({
  name: 'security',
  initialState,
  reducers: {
    /**
     * Cancel an in-progress MFA enrollment — the user clicked Cancel on
     * the QR-code step. Drops the challenge and resets confirm state so
     * the card returns to the "Enable MFA" CTA.
     */
    cancelMfaEnrollment(state) {
      state.mfaChallenge = null;
      state.mfaEnableStatus = 'idle';
      state.mfaEnableError = null;
      state.mfaConfirmStatus = 'idle';
      state.mfaConfirmError = null;
      state.mfaUiStep = state.mfaStatus?.isEnabled ? 'enabled' : 'disabled';
    },
    /**
     * Toggle the disable-MFA confirmation form open/closed. The view
     * uses this to show/hide the TOTP input below the "Disable MFA"
     * button without managing the flag locally.
     */
    setMfaUiStep(state, action: PayloadAction<MfaUiStep>) {
      state.mfaUiStep = action.payload;
    },
    /**
     * Clear the backup codes from state — called when the customer
     * dismisses the backup-codes dialog after acknowledging them.
     */
    clearMfaBackupCodes(state) {
      state.mfaBackupCodes = null;
    },
    /**
     * Clear an inline error after the user dismisses it / re-tries.
     */
    clearMfaConfirmError(state) {
      state.mfaConfirmError = null;
      if (state.mfaConfirmStatus === 'failed') state.mfaConfirmStatus = 'idle';
    },
    clearMfaDisableError(state) {
      state.mfaDisableError = null;
      if (state.mfaDisableStatus === 'failed') state.mfaDisableStatus = 'idle';
    },
    /** Clear a per-device revoke error after dismissal. */
    clearTrustedDeviceRevokeError(state, action: PayloadAction<number>) {
      delete state.trustedDeviceRevokeError[action.payload];
      if (state.trustedDeviceRevokeStatus[action.payload] === 'failed') {
        delete state.trustedDeviceRevokeStatus[action.payload];
      }
    },
    /** Reset the slice to its initial shape — used by logout flows. */
    resetSecurityState() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      // -- fetchMfaStatus -------------------------------------------------
      .addCase(fetchMfaStatus.pending, (state) => {
        state.mfaStatusFetch = 'loading';
        state.mfaStatusError = null;
      })
      .addCase(fetchMfaStatus.fulfilled, (state, action) => {
        state.mfaStatusFetch = 'succeeded';
        state.mfaStatus = action.payload;
        // Only adjust the UI step if we are not in the middle of an
        // enrollment / disable flow (those preserve their own step).
        if (state.mfaUiStep === 'disabled' || state.mfaUiStep === 'enabled') {
          state.mfaUiStep = action.payload.isEnabled ? 'enabled' : 'disabled';
        }
      })
      .addCase(fetchMfaStatus.rejected, (state, action) => {
        state.mfaStatusFetch = 'failed';
        state.mfaStatusError =
          action.payload ?? { message: action.error.message ?? 'Failed to load MFA status' };
      })

      // -- enableMfa ------------------------------------------------------
      .addCase(enableMfa.pending, (state) => {
        state.mfaEnableStatus = 'loading';
        state.mfaEnableError = null;
      })
      .addCase(enableMfa.fulfilled, (state, action) => {
        state.mfaEnableStatus = 'succeeded';
        state.mfaChallenge = action.payload;
        state.mfaUiStep = 'provisioned';
      })
      .addCase(enableMfa.rejected, (state, action) => {
        state.mfaEnableStatus = 'failed';
        state.mfaEnableError =
          action.payload ?? { message: action.error.message ?? 'Failed to start MFA enrollment' };
      })

      // -- confirmMfa -----------------------------------------------------
      .addCase(confirmMfa.pending, (state) => {
        state.mfaConfirmStatus = 'loading';
        state.mfaConfirmError = null;
      })
      .addCase(confirmMfa.fulfilled, (state, action) => {
        state.mfaConfirmStatus = 'succeeded';
        state.mfaBackupCodes = action.payload.backupCodes;
        state.mfaChallenge = null;
        state.mfaStatus = {
          isEnabled: true,
          confirmedAt: new Date().toISOString(),
        };
        state.mfaUiStep = 'enabled';
      })
      .addCase(confirmMfa.rejected, (state, action) => {
        state.mfaConfirmStatus = 'failed';
        state.mfaConfirmError =
          action.payload ?? { message: action.error.message ?? 'Invalid verification code' };
        // Do NOT reset the UI step or challenge — the user stays on the
        // QR-code screen so they can re-enter the code without losing
        // the secret.
      })

      // -- disableMfa -----------------------------------------------------
      .addCase(disableMfa.pending, (state) => {
        state.mfaDisableStatus = 'loading';
        state.mfaDisableError = null;
      })
      .addCase(disableMfa.fulfilled, (state) => {
        state.mfaDisableStatus = 'succeeded';
        state.mfaStatus = { isEnabled: false, confirmedAt: null };
        state.mfaUiStep = 'disabled';
        // Server-side, disabling MFA invalidates all trusted-device
        // tokens. Mirror that by clearing the local list — the view
        // will re-fetch but the row removal should not lag the UI.
        state.trustedDevices = [];
      })
      .addCase(disableMfa.rejected, (state, action) => {
        state.mfaDisableStatus = 'failed';
        state.mfaDisableError =
          action.payload ?? { message: action.error.message ?? 'Failed to disable MFA' };
      })

      // -- fetchTrustedDevices --------------------------------------------
      .addCase(fetchTrustedDevices.pending, (state) => {
        state.trustedDevicesStatus = 'loading';
        state.trustedDevicesError = null;
      })
      .addCase(fetchTrustedDevices.fulfilled, (state, action) => {
        state.trustedDevicesStatus = 'succeeded';
        state.trustedDevices = action.payload.devices;
      })
      .addCase(fetchTrustedDevices.rejected, (state, action) => {
        state.trustedDevicesStatus = 'failed';
        state.trustedDevicesError =
          action.payload ?? {
            message: action.error.message ?? 'Failed to load trusted devices',
          };
      })

      // -- revokeTrustedDevice (optimistic) -------------------------------
      .addCase(revokeTrustedDevice.pending, (state, action) => {
        const id = action.meta.arg;
        state.trustedDeviceRevokeStatus[id] = 'loading';
        state.trustedDeviceRevokeError[id] = null;
        // Optimistic removal — drop the device from the visible list
        // immediately. The `rejected` reducer reinstates it.
        state.trustedDevices = state.trustedDevices.filter((d) => d.id !== id);
      })
      .addCase(revokeTrustedDevice.fulfilled, (state, action) => {
        const id = action.payload.id;
        state.trustedDeviceRevokeStatus[id] = 'succeeded';
      })
      .addCase(revokeTrustedDevice.rejected, (state, action) => {
        const id = action.meta.arg;
        state.trustedDeviceRevokeStatus[id] = 'failed';
        state.trustedDeviceRevokeError[id] =
          action.payload ?? {
            message: action.error.message ?? 'Failed to revoke trusted device',
          };
        // The fetched list will be refreshed by the view. We don't
        // re-insert the device record from `meta.arg` because we'd
        // lose its full DTO; the view re-fetches via fetchTrustedDevices.
      });
  },
});

// ----------------------------------------------------------------------------
// Exports
// ----------------------------------------------------------------------------

export const {
  cancelMfaEnrollment,
  setMfaUiStep,
  clearMfaBackupCodes,
  clearMfaConfirmError,
  clearMfaDisableError,
  clearTrustedDeviceRevokeError,
  resetSecurityState,
} = securitySlice.actions;

export const securityReducer = securitySlice.reducer;
export { securitySlice };

// ----------------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------------

interface SecuritySliceRoot {
  security: SecurityState;
}

export const selectSecurityState = (state: SecuritySliceRoot): SecurityState => state.security;

export const selectMfaStatus = (state: SecuritySliceRoot): AccountMfaStatus | null =>
  state.security.mfaStatus;

export const selectMfaStatusFetch = (state: SecuritySliceRoot): SecuritySectionStatus =>
  state.security.mfaStatusFetch;

export const selectMfaStatusError = (state: SecuritySliceRoot): SecuritySectionError | null =>
  state.security.mfaStatusError;

export const selectMfaChallenge = (state: SecuritySliceRoot): MfaEnrollmentChallenge | null =>
  state.security.mfaChallenge;

export const selectMfaEnableStatus = (state: SecuritySliceRoot): SecuritySectionStatus =>
  state.security.mfaEnableStatus;

export const selectMfaEnableError = (state: SecuritySliceRoot): SecuritySectionError | null =>
  state.security.mfaEnableError;

export const selectMfaConfirmStatus = (state: SecuritySliceRoot): SecuritySectionStatus =>
  state.security.mfaConfirmStatus;

export const selectMfaConfirmError = (state: SecuritySliceRoot): SecuritySectionError | null =>
  state.security.mfaConfirmError;

export const selectMfaDisableStatus = (state: SecuritySliceRoot): SecuritySectionStatus =>
  state.security.mfaDisableStatus;

export const selectMfaDisableError = (state: SecuritySliceRoot): SecuritySectionError | null =>
  state.security.mfaDisableError;

export const selectMfaBackupCodes = (state: SecuritySliceRoot): string[] | null =>
  state.security.mfaBackupCodes;

export const selectMfaUiStep = (state: SecuritySliceRoot): MfaUiStep =>
  state.security.mfaUiStep;

export const selectTrustedDevices = (state: SecuritySliceRoot): TrustedDevice[] =>
  state.security.trustedDevices;

export const selectTrustedDevicesStatus = (state: SecuritySliceRoot): SecuritySectionStatus =>
  state.security.trustedDevicesStatus;

export const selectTrustedDevicesError = (state: SecuritySliceRoot): SecuritySectionError | null =>
  state.security.trustedDevicesError;

export const selectTrustedDeviceRevokeStatus = (
  state: SecuritySliceRoot,
): Record<number, SecuritySectionStatus> => state.security.trustedDeviceRevokeStatus;

export const selectTrustedDeviceRevokeError = (
  state: SecuritySliceRoot,
): Record<number, SecuritySectionError | null> => state.security.trustedDeviceRevokeError;

/**
 * `true` while *any* MFA-flow thunk is pending. Useful for blanket-
 * disabling controls while the slice is mid-action.
 */
export const selectMfaAnyPending = createSelector(
  [
    selectMfaStatusFetch,
    selectMfaEnableStatus,
    selectMfaConfirmStatus,
    selectMfaDisableStatus,
  ],
  (a, b, c, d) =>
    a === 'loading' || b === 'loading' || c === 'loading' || d === 'loading',
);
